import { useRef, useState, useLayoutEffect, useCallback } from "react";
import { Tooltip } from "@/components/Tooltip";
import { cn } from "@/utils/cn";

interface PillOverflowListProps {
  items: string[];
  advisorId: string;
  pillClassName?: string;
}

const GAP = 8; // gap-2

export const PillOverflowList = ({
  items: itemsProp,
  advisorId,
  pillClassName,
}: PillOverflowListProps) => {
  const items = itemsProp ?? [];
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(items.length);

  const calculate = useCallback(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const containerWidth = container.clientWidth;
    if (!containerWidth) return;

    const pillEls = Array.from(measure.querySelectorAll<HTMLElement>("[data-pill]"));
    const moreEl = measure.querySelector<HTMLElement>("[data-more]");
    const pillWidths = pillEls.map((el) => el.offsetWidth);
    const moreWidth = moreEl ? moreEl.offsetWidth : 50;

    let totalWidth = 0;
    let count = 0;

    for (let i = 0; i < pillWidths.length; i++) {
      const addWidth = (i === 0 ? 0 : GAP) + pillWidths[i];
      const tentative = totalWidth + addWidth;
      const hasMore = i < pillWidths.length - 1;
      const spaceNeeded = hasMore ? tentative + GAP + moreWidth : tentative;

      if (spaceNeeded <= containerWidth) {
        totalWidth = tentative;
        count = i + 1;
      } else {
        break;
      }
    }

    setVisibleCount(Math.max(1, count));
  }, []);

  useLayoutEffect(() => {
    calculate();
    const observer = new ResizeObserver(calculate);
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [calculate, items]);

  const pillClass = cn(
    "whitespace-nowrap rounded-lg border border-pillWarm-450 dark:border-pab-dark-pill-border bg-pillWarm-350 dark:bg-pab-dark-pill-bg px-2 py-1 text-12 text-pillWarm-650 dark:text-pab-dark-pill-text",
    pillClassName,
  );

  const visibleItems = items.slice(0, visibleCount);
  const hiddenItems = items.slice(visibleCount);

  return (
    <div
      ref={containerRef}
      className="relative flex flex-nowrap items-center gap-2 overflow-hidden"
    >
      {/* Hidden measurement layer — renders all pills + "+99 more" off-screen to measure widths */}
      <div
        ref={measureRef}
        className="pointer-events-none invisible absolute left-0 top-0 flex flex-nowrap gap-2"
        aria-hidden="true"
      >
        {items.map((item, i) => (
          <span key={i} data-pill className={pillClass}>
            {item}
          </span>
        ))}
        <span data-more className="whitespace-nowrap px-1 text-12 text-pillWarm-650">
          +{items.length} more
        </span>
      </div>

      {visibleItems.map((item) => (
        <span key={`${advisorId}-${item}`} className={cn("shrink-0", pillClass)}>
          {item}
        </span>
      ))}

      {hiddenItems.length > 0 && (
        <Tooltip
          content={
            <div className="flex flex-wrap gap-1.5">
              {hiddenItems.map((item) => (
                <span
                  key={item}
                  className="whitespace-nowrap rounded border border-pillWarm-450 dark:border-pab-dark-pill-border bg-pillWarm-350 dark:bg-pab-dark-pill-bg px-1.5 py-0.5 text-[11px] text-pillWarm-650 dark:text-pab-dark-pill-text"
                >
                  {item}
                </span>
              ))}
            </div>
          }
          variant="light"
          side="top"
          showArrow={false}
        >
          <span className="shrink-0 cursor-default whitespace-nowrap px-1 text-12 text-pillWarm-650 dark:text-pab-dark-pill-text underline-offset-2 hover:underline">
            +{hiddenItems.length} more
          </span>
        </Tooltip>
      )}
    </div>
  );
};
